import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translations } from '../i18n/translations';
import type { TranslationResource } from '../i18n/translations.d';

export type RouteKey = keyof TranslationResource['routes'];

const SITE_URL = 'https://usewai.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo/wai-final-d1p-serif-filled_navy_white-square-512.png`;

const ROUTE_PATHS: Record<RouteKey, string> = {
  backToOverview: '/',
  fullPlatformCard: '/',
  home: '/',
  platform: '/plataforma',
  autonomous: '/agentes-autonomos',
  security: '/seguridad',
  partners: '/partners',
  demo: '/demo',
  connectors: '/conectores',
  pricing: '/precios',
};

function setMeta(selector: string, attr: 'content' | 'href', value: string, createTag?: 'meta' | 'link') {
  let tag = document.head.querySelector(selector);
  if (!tag && createTag) {
    tag = document.createElement(createTag);
    const match = selector.match(/^(meta|link)\[(name|property|rel)="([^"]+)"\]$/);
    if (match) {
      tag.setAttribute(match[2], match[3]);
    }
    document.head.appendChild(tag);
  }
  if (tag) tag.setAttribute(attr, value);
}

export function useRouteMeta(routeKey: RouteKey) {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang: 'es' | 'en' = i18n.language && i18n.language.startsWith('en') ? 'en' : 'es';
    const ns = translations[lang].translation.routes as TranslationResource['routes'];
    const data = (ns[routeKey] as unknown) as { title?: string; description?: string };
    if (!data || !data.title || !data.description) return;

    const path = ROUTE_PATHS[routeKey] || '/';
    const canonical = `${SITE_URL}${path}`;

    document.title = data.title;
    setMeta('meta[name="description"]', 'content', data.description, 'meta');
    setMeta('meta[property="og:title"]', 'content', data.title, 'meta');
    setMeta('meta[property="og:description"]', 'content', data.description, 'meta');
    setMeta('meta[property="og:url"]', 'content', canonical, 'meta');
    setMeta('meta[property="og:image"]', 'content', DEFAULT_OG_IMAGE, 'meta');
    setMeta('meta[property="og:locale"]', 'content', lang === 'en' ? 'en_US' : 'es_ES', 'meta');
    setMeta('meta[name="twitter:title"]', 'content', data.title, 'meta');
    setMeta('meta[name="twitter:description"]', 'content', data.description, 'meta');
    setMeta('meta[name="twitter:image"]', 'content', DEFAULT_OG_IMAGE, 'meta');
    setMeta('link[rel="canonical"]', 'href', canonical, 'link');
  }, [routeKey, i18n.language]);
}

export const routePaths = ROUTE_PATHS;
